import { useCallback, useEffect, useState } from 'react'
import { kycRepository } from '../../data/repositories/KycRepositoryImpl'
import { KycListItem } from '../../domain/entities/Kyc'
import { KycStatusFilter } from '../../domain/repositories/KycRepository'

export const useKyc = (initialLimit = 10) => {
  const [records, setRecords] = useState<KycListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [status, setStatus] = useState<KycStatusFilter>('all')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchRecords = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await kycRepository.getKycRecords({ status, page, limit: initialLimit })
      setRecords(res.records)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load KYC records.')
    } finally {
      setLoading(false)
    }
  }, [status, page, initialLimit])

  useEffect(() => {
    fetchRecords()
  }, [fetchRecords])

  const approveKyc = async (kycId: string) => {
    setError(null)
    try {
      await kycRepository.verifyKyc(kycId)
      await fetchRecords()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to approve KYC.')
      throw err
    }
  }

  const rejectKyc = async (kycId: string, reason: string) => {
    setError(null)
    try {
      await kycRepository.rejectKyc(kycId, reason)
      await fetchRecords()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to reject KYC.')
      throw err
    }
  }

  const resetFilters = () => {
    setStatus('all')
    setPage(1)
  }

  return {
    records,
    loading,
    error,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    approveKyc,
    rejectKyc,
    resetFilters,
    refresh: fetchRecords,
  }
}
